import { useEffect, useRef, useState } from "react";
import { IoBarcodeOutline, IoCameraOutline, IoClose, IoFlashlight, IoFlashlightOutline, IoRefreshOutline, IoWarningOutline } from "react-icons/io5";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { isInAppBrowser } from "@/utils/image";
import { tapSuccess } from "@/utils/haptics";
import api from "@/api";

// Uses BarcodeDetector API on Chrome/Android; falls back to @zxing/browser on iOS Safari.
export const BarcodeScanner = ({ open, onClose, onDetected, allowMultiMode = false, initialMode = "single" }) => {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const runningRef = useRef(false);
  const zxingControlsRef = useRef(null);
  const [cameraOk, setCameraOk] = useState(true);
  const [cameraStarting, setCameraStarting] = useState(true);
  const [cameraError, setCameraError] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [manual, setManual] = useState("");
  const [inApp] = useState(() => isInAppBrowser());
  const [attempt, setAttempt] = useState(0);
  const [mode, setMode] = useState(initialMode);
  const [multiCount, setMultiCount] = useState(0);
  const [ocrBusy, setOcrBusy] = useState(false);
  const ocrInputRef = useRef(null);

  // Keep the latest onDetected in a ref so re-renders of the parent (which typically
  // pass a fresh arrow function) don't tear down + rebuild the camera pipeline. This
  // was the root cause of the "sometimes the scanner freezes after a retry" bug.
  const onDetectedRef = useRef(onDetected);
  useEffect(() => { onDetectedRef.current = onDetected; }, [onDetected]);
  const modeRef = useRef(mode);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { if (!open) setMultiCount(0); }, [open]);

  const emitDetection = (value) => {
    if (modeRef.current === "multi") {
      setMultiCount((n) => n + 1);
      // In multi mode, keep the scanner running and let parent handle a quick-add.
      // We restart the detection loop right after the callback returns.
      Promise.resolve(onDetectedRef.current(value, "multi")).finally(() => {
        // Reset the ZXing/native running flag so the next scan is captured.
        runningRef.current = true;
      });
      return;
    }
    onDetectedRef.current(value, "single");
  };

  /** OCR fallback: user shoots the numeric code printed under the barcode.
   *  We send the photo to Gemini Vision which extracts the digits. */
  const handleOcrPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/ocr/barcode-number", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.code) {
        tapSuccess();
        toast.success(`Codice letto: ${data.code}`);
        emitDetection(String(data.code));
      } else {
        toast.error("Numero non riconosciuto. Riprova con foto più nitida o inseriscilo a mano.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OCR fallito, riprova");
    } finally {
      setOcrBusy(false);
    }
  };

  const stopEverything = () => {
    runningRef.current = false;
    try { zxingControlsRef.current?.stop(); } catch { /* noop */ }
    zxingControlsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => { try { tr.stop(); } catch { /* noop */ } });
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCameraOk(true);
    setCameraError("");
    setCameraStarting(true);
    setTorchOn(false);
    setTorchSupported(false);

    const tryGetStream = async () => {
      // Try environment first, then any, then user
      const candidates = [
        { video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
        { video: { facingMode: "environment" }, audio: false },
        { video: true, audio: false },
      ];
      let lastErr;
      for (const c of candidates) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(c);
          return stream;
        } catch (e) {
          lastErr = e;
          if (e?.name === "NotAllowedError") throw e; // permission → don't retry
        }
      }
      throw lastErr || new Error("Camera unavailable");
    };

    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw Object.assign(new Error("No mediaDevices"), { name: "NotSupportedError" });
        }
        const stream = await tryGetStream();
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute("playsinline", "true");
          videoRef.current.muted = true;
          await videoRef.current.play().catch(() => null);
        }

        // Check torch capability
        try {
          const track = stream.getVideoTracks()[0];
          const caps = track.getCapabilities?.() || {};
          if (caps.torch) setTorchSupported(true);
        } catch { /* noop */ }

        setCameraStarting(false);

        const nativeDetector =
          "BarcodeDetector" in window
            ? new window.BarcodeDetector({
                formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39"],
              })
            : null;

        if (nativeDetector) {
          runningRef.current = true;
          const loop = async () => {
            if (!runningRef.current || !videoRef.current) return;
            try {
              const codes = await nativeDetector.detect(videoRef.current);
              if (codes && codes.length > 0) {
                runningRef.current = false;
                tapSuccess();
                emitDetection(codes[0].rawValue);
                // In multi mode, requestAnimationFrame will re-check runningRef which is set true by parent.
                // Add debounce to avoid duplicate captures of the same barcode.
                if (modeRef.current === "multi") {
                  await new Promise((r) => setTimeout(r, 1200));
                  requestAnimationFrame(loop);
                }
                return;
              }
            } catch (err) {
              if (err?.name !== "NotSupportedError") {
                console.debug("Barcode detect frame skipped:", err?.message || err);
              }
            }
            requestAnimationFrame(loop);
          };
          loop();
        } else {
          // iOS Safari / non-Chromium fallback: use @zxing/browser
          const reader = new BrowserMultiFormatReader();
          try {
            const controls = await reader.decodeFromStream(
              stream,
              videoRef.current,
              (result, err, ctrls) => {
                if (cancelled) return;
                if (result) {
                  if (modeRef.current === "single") {
                    try { ctrls?.stop(); } catch { /* noop */ }
                  }
                  tapSuccess();
                  emitDetection(result.getText());
                }
              },
            );
            zxingControlsRef.current = controls;
          } catch (e) {
            console.debug("zxing scan failed", e?.message || e);
          }
        }
      } catch (e) {
        console.debug("Camera error:", e?.name, e?.message);
        setCameraOk(false);
        setCameraStarting(false);
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          setCameraError(
            "Permesso fotocamera negato. Vai nelle impostazioni del browser, autorizza la fotocamera per questo sito e riprova.",
          );
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setCameraError("Nessuna fotocamera disponibile su questo dispositivo.");
        } else if (name === "NotReadableError") {
          setCameraError("Fotocamera in uso da un'altra app. Chiudi le altre app e riprova.");
        } else if (name === "NotSupportedError") {
          setCameraError("Il browser non supporta la fotocamera. Prova con Safari o Chrome.");
        } else {
          setCameraError("Impossibile accedere alla fotocamera. Inserisci l'EAN a mano qui sotto.");
        }
      }
    };
    start();

    return () => {
      cancelled = true;
      stopEverything();
    };
  }, [open, attempt]);

  const toggleTorch = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch {
      toast.error("Torcia non disponibile su questo dispositivo");
      setTorchSupported(false);
    }
  };

  const retry = () => {
    setAttempt((n) => n + 1);
  };

  if (!open) return null;

  return (
    <div
      data-testid="barcode-scanner"
      className="tf-scanner-overlay"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="tf-scanner-sheet"
      >
        <div className="tf-drawer-handle" />
        <div className="tf-between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div className="tf-h2">{t("scan_barcode")}</div>
            {mode === "multi" && multiCount > 0 && (
              <span className="tf-scanner-multi-counter" data-testid="scanner-multi-counter">
                +{multiCount}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {torchSupported && cameraOk && (
              <button
                data-testid="scanner-torch"
                className={`tf-icon-btn ${torchOn ? "yellow" : ""}`}
                onClick={toggleTorch}
                aria-label="Torcia"
                title="Torcia"
              >
                {torchOn ? <IoFlashlight size={20} /> : <IoFlashlightOutline size={20} />}
              </button>
            )}
            <button
              data-testid="scanner-close"
              className="tf-icon-btn"
              onClick={onClose}
              aria-label="Chiudi"
            >
              <IoClose size={20} />
            </button>
          </div>
        </div>

        {allowMultiMode && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            <div className="tf-scanner-mode-toggle" role="tablist" aria-label="Modalità scansione">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "single"}
                data-testid="scanner-mode-single"
                className={`tf-scanner-mode-btn ${mode === "single" ? "active" : ""}`}
                onClick={() => setMode("single")}
              >
                Singolo
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "multi"}
                data-testid="scanner-mode-multi"
                className={`tf-scanner-mode-btn ${mode === "multi" ? "active" : ""}`}
                onClick={() => setMode("multi")}
              >
                Multipli
              </button>
            </div>
          </div>
        )}

        {inApp && (
          <div
            data-testid="scanner-inapp-warning"
            style={{
              background: "var(--tf-yellow-soft)",
              color: "#92400E",
              padding: "10px 12px",
              borderRadius: 12,
              fontSize: 13,
              lineHeight: 1.35,
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              marginBottom: 10,
            }}
          >
            <IoWarningOutline size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Stai aprendo l&apos;app dentro un altro social. Per far funzionare la fotocamera,
              apri il link in <b>Safari</b> o <b>Chrome</b>.
            </span>
          </div>
        )}

        {cameraOk ? (
          <div className="tf-scanner-viewport" data-testid="scanner-viewport">
            <video ref={videoRef} className="tf-scanner-video" playsInline muted autoPlay />
            {cameraStarting && (
              <div className="tf-scanner-loading" data-testid="scanner-loading">
                <div className="tf-scanner-spinner" />
                <div style={{ fontSize: 13, marginTop: 8 }}>Apro la fotocamera…</div>
              </div>
            )}
            {!cameraStarting && (
              <>
                <div className="tf-scanner-frame" aria-hidden="true">
                  <span className="corner tl" />
                  <span className="corner tr" />
                  <span className="corner bl" />
                  <span className="corner br" />
                </div>
                <div className="tf-scanner-line" aria-hidden="true" />
              </>
            )}
          </div>
        ) : (
          <div
            data-testid="scanner-error"
            style={{
              background: "var(--tf-red-soft)",
              color: "var(--tf-red)",
              padding: "14px",
              borderRadius: 14,
              fontSize: 13,
              lineHeight: 1.4,
              marginBottom: 4,
            }}
          >
            {cameraError}
          </div>
        )}

        {!cameraOk && (
          <button
            type="button"
            data-testid="scanner-retry"
            className="tf-btn-ghost"
            onClick={retry}
            style={{ marginTop: 8 }}
          >
            <IoRefreshOutline size={18} /> Riprova ad aprire la fotocamera
          </button>
        )}

        {cameraOk && (
          <p className="tf-small" style={{ marginTop: 10, textAlign: "center", color: "var(--tf-muted)" }}>
            {t("barcode_hint")}
          </p>
        )}

        {/* OCR fallback: read the numeric code printed under the barcode via Gemini Vision.
            Useful when the visual scanner cannot decode the pattern (bad lighting, damaged label). */}
        <input
          ref={ocrInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          data-testid="scanner-ocr-file"
          onChange={handleOcrPick}
        />
        <button
          type="button"
          data-testid="scanner-ocr-photo"
          className="tf-btn-ghost"
          onClick={() => ocrInputRef.current?.click()}
          disabled={ocrBusy}
          style={{ marginTop: 10, width: "100%", justifyContent: "center" }}
        >
          <IoCameraOutline size={18} />
          {ocrBusy ? "Lettura del numero…" : "Non legge? Fotografa il numero sotto"}
        </button>

        <div className="tf-field" style={{ marginTop: 16 }}>
          <label className="tf-label">Oppure inserisci EAN a mano</label>
          <input
            data-testid="manual-barcode-input"
            className="tf-input"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Es. 8001120450081"
            inputMode="numeric"
            autoComplete="off"
          />
        </div>
        <button
          data-testid="manual-barcode-submit"
          className="tf-btn-primary"
          disabled={!manual.trim()}
          onClick={() => {
            const code = manual.trim();
            setManual("");
            emitDetection(code);
          }}
        >
          <IoBarcodeOutline size={18} />
          {t("confirm")}
        </button>
      </div>
    </div>
  );
};
